import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { ApiError, canDelete, type LifecycleAction, type LifecycleKind, type Org } from '../../lib/api';
import { Button } from '../../ui';
import { useSession } from '../auth/session';
import { Section } from './cards';
import { useBoards, useInvalidateContent, useProjects } from './queries';

type Row = { kind: LifecycleKind; id: string; title: string; createdBy: string | null; deletedAt: string };

/** Trashed projects and boards (purged automatically after 30 days): restore, or delete forever (admins). */
export function TrashPage({ org }: { org: Org }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const session = useSession();
  const invalidate = useInvalidateContent();
  const projects = useProjects(org.id, true);
  const boards = useBoards({ orgId: org.id, trashed: true });
  const [error, setError] = useState<string | null>(null);
  const userId = session.status === 'signed-in' ? session.user.id : '';

  const rows: Row[] = [
    ...(projects.data ?? []).map((p) => ({ kind: 'project' as const, id: p.id, title: p.name, createdBy: p.createdBy, deletedAt: p.deletedAt! })),
    ...(boards.data ?? []).map((b) => ({ kind: 'board' as const, id: b.id, title: b.title, createdBy: b.createdBy, deletedAt: b.deletedAt! })),
  ];

  const run = async (row: Row, action: LifecycleAction) => {
    setError(null);
    try {
      await api.lifecycle(row.kind, action, row.id);
      await invalidate();
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'conflict' ? t('dashboard.restoreProjectFirst') : t('auth.genericError'));
    }
  };

  return (
    <Section title={t('dashboard.trash')} empty={projects.isSuccess && boards.isSuccess && rows.length === 0} emptyBody={t('dashboard.emptyBody.trash')}>
      <p className="font-sans text-sm text-fg-muted">{t('dashboard.trashNotice')}</p>
      {error && (
        <p role="alert" className="font-sans text-xs text-danger">
          {error}
        </p>
      )}
      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <li key={`${row.kind}:${row.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
            <span className="flex flex-col">
              <span className="font-display font-bold">{row.title}</span>
              <span className="font-mono text-xs text-fg-muted">
                {t(`dashboard.kind.${row.kind}`)} · {new Date(row.deletedAt).toLocaleDateString(i18n.language)}
              </span>
            </span>
            <span className="flex gap-2">
              {canDelete(org.role, row.createdBy, userId) && (
                <Button onClick={() => run(row, 'restore')}>
                  {t('dashboard.restore')}
                </Button>
              )}
              {org.role !== 'member' && (
                <Button onClick={() => run(row, 'purge')}>
                  {t('dashboard.purge')}
                </Button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}
